<template>
  <aside class="kn-sidebar" :class="{'is-open': open, 'is-collapsed': sidebarCollapsed}">
    <div class="kn-sidebar__brand">
      <router-link
        v-show="!sidebarCollapsed"
        :to="{name: 'Home'}"
        class="kn-sidebar__logo"
        @click.native="$emit('close')"
      >
        <img alt="Главная" src="@/assets/img/logo.png"/>
      </router-link>
      <button type="button" class="kn-icon-btn kn-sidebar__close" aria-label="Закрыть меню" @click="$emit('close')">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <nav class="kn-sidebar__nav" @click="onNavClick">
      <menu-component></menu-component>
    </nav>

    <div class="kn-sidebar__footer">
      <div v-if="version && !sidebarCollapsed" class="kn-sidebar__version">Версия {{ version }}</div>
      <el-tooltip :content="sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'" placement="right" :open-delay="300">
        <button
          type="button"
          class="kn-icon-btn kn-sidebar__collapse"
          :aria-label="sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'"
          @click="toggleCollapsed"
        >
          <i :class="sidebarCollapsed ? 'fas fa-angle-double-right' : 'fas fa-angle-double-left'"></i>
        </button>
      </el-tooltip>
    </div>
  </aside>
</template>

<script>
import {mapGetters} from "vuex";
import MenuComponent from "@/components/header/MenuComponent.vue";

const NARROW_QUERY = '(max-width: 1023px)';

export default {
  name: "app-sidebar",
  components: {MenuComponent},
  props: {
    open: {type: Boolean, default: false},
  },
  data() {
    return {
      version: process.env.PACKAGE_VERSION,
      mediaQuery: null,
    }
  },
  computed: {
    ...mapGetters('settings', ['sidebarCollapsed']),
  },
  mounted() {
    this.mediaQuery = window.matchMedia(NARROW_QUERY);
    this.onViewportChange();
    this.mediaQuery.addEventListener('change', this.onViewportChange);
  },
  beforeDestroy() {
    this.mediaQuery && this.mediaQuery.removeEventListener('change', this.onViewportChange);
  },
  methods: {
    onViewportChange() {
      this.$store.commit('settings/setViewportNarrow', this.mediaQuery.matches);
    },
    toggleCollapsed() {
      this.$store.commit('settings/setSidebarCollapsed', !this.sidebarCollapsed);
    },
    onNavClick(event) {
      if (event.target.closest('.menu-router-link')) {
        this.$emit('close');
      }
    },
  },
}
</script>

<style lang="scss">
.kn-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  width: var(--kn-sidebar-width);
  background-color: var(--kn-sidebar-bg);
  border-right: 1px solid var(--kn-border);
  box-sizing: border-box;
  transition: width .2s ease;

  &__brand {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: var(--kn-topbar-height);
    padding: 0 20px 0 24px;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__logo img {
    display: block;
    height: 22px;
    width: auto;
  }

  &__close {
    display: none !important;
  }

  &__nav {
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 8px 12px 16px;

    .el-menu {
      border-right: 0;
      background-color: transparent;
    }

    .el-menu-item,
    .el-submenu__title {
      height: auto;
      min-height: 40px;
      min-width: 0 !important;
      line-height: 1.35;
      white-space: normal !important;
      overflow: hidden;
      overflow-wrap: anywhere;
      word-break: break-word;
      margin-bottom: 2px;
      padding: 0 12px !important;
      border-radius: var(--kn-radius-btn);
      color: var(--kn-text-muted);
      font-size: 14px;
      font-weight: 500;
      border: 0 !important;
      background-color: transparent;
      transition: background-color .15s ease, color .15s ease;

      &:hover, &:focus {
        background-color: var(--kn-hover);
        color: var(--kn-text);
      }

      .el-submenu__icon-arrow {
        right: 12px;
        color: var(--kn-text-faint);
      }
    }

    .el-menu-item .menu-router-link {
      display: flex;
      align-items: flex-start;
      min-width: 0;
      min-height: inherit;
      padding: 9px 0;
      box-sizing: border-box;
      color: inherit;
      text-decoration: none;
    }

    .el-submenu__title {
      display: flex;
      align-items: center;
      padding-top: 9px !important;
      padding-bottom: 9px !important;
      padding-right: 32px !important;
      box-sizing: border-box;
    }

    .menu-router-link-text,
    .el-submenu__title > span {
      min-width: 0;
      flex: 1 1 auto;
      overflow-wrap: anywhere;
      word-break: break-word;
      white-space: normal;
    }

    .el-menu--inline {
      padding: 2px 0 4px;

      .el-menu-item,
      .el-submenu__title {
        min-height: 36px;
        padding-left: 44px !important;
        font-size: 13px;
      }

      .el-menu-item .menu-router-link,
      .el-submenu__title {
        padding-top: 7px !important;
        padding-bottom: 7px !important;
      }

      .el-menu--inline .el-menu-item {
        padding-left: 60px !important;
      }
    }

    .el-menu-item.is-active {
      background-color: var(--kn-primary-soft);
      color: var(--kn-primary);
      font-weight: 600;

      .menu-router-link-icon { color: var(--kn-primary); }
    }

    .el-submenu.is-active > .el-submenu__title {
      color: var(--kn-text);
      background-color: transparent;

      .menu-router-link-icon { color: var(--kn-primary); }
    }

    .el-menu--collapse {
      width: 100%;

      > .el-menu-item,
      > .el-submenu > .el-submenu__title {
        justify-content: center;
        padding: 0 !important;

        .menu-router-link { justify-content: center; }
        .menu-router-link-text, > span, .el-submenu__icon-arrow { display: none; }
        .menu-router-link-icon { margin-right: 0; }
      }

      > .el-submenu.is-active > .el-submenu__title {
        background-color: var(--kn-primary-soft);
      }
    }
  }

  .menu-router-link-icon,
  &__nav .menu-router-link-icon {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 20px;
    margin-right: 12px;
    font-size: 15px;
    color: var(--kn-text-faint);
    transition: color .15s ease;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px 12px 24px;
    border-top: 1px solid var(--kn-border);
  }

  &__version {
    font-size: 12px;
    color: var(--kn-text-faint);
    white-space: nowrap;
  }

  &.is-collapsed {
    width: var(--kn-sidebar-collapsed-width);

    .kn-sidebar__brand {
      justify-content: center;
      padding: 0;
      height: 16px;
    }

    .kn-sidebar__logo {
      display: none !important;
    }

    .kn-sidebar__nav { padding: 8px 10px 16px; }

    .kn-sidebar__footer {
      justify-content: center;
      padding: 10px 0 12px;
    }
  }
}

body.designV2 .el-menu--vertical.el-menu--popup-right-start,
body.designV2 .el-menu--vertical > .el-menu--popup {
  padding: 6px;
  border: 1px solid var(--kn-border);
  border-radius: var(--kn-radius-btn);
  background-color: var(--kn-card);
  box-shadow: var(--kn-shadow-pop);

  .el-menu-item, .el-submenu__title {
    height: 36px;
    line-height: 36px;
    padding: 0 12px !important;
    border-radius: 8px;
    background-color: transparent;
    color: var(--kn-text);

    &:hover { background-color: var(--kn-hover); }
  }

  .el-menu-item.is-active {
    background-color: var(--kn-primary-soft);
    color: var(--kn-primary);
  }

  .menu-router-link { color: inherit; text-decoration: none; display: block; }
}

[data-theme="dark"] .kn-sidebar__logo img,
[data-theme="dark"] .kn-login__logo img {
  filter: invert(1) hue-rotate(180deg) brightness(1.1);
}

@media (max-width: 1023px) {
  .kn-sidebar {
    transform: translateX(-100%);
    transition: transform .25s ease;
    box-shadow: none;

    &.is-open {
      transform: translateX(0);
      box-shadow: var(--kn-shadow-modal);
    }

    &__close {
      display: inline-flex !important;
    }

    &__collapse {
      display: none !important;
    }
  }
}
</style>
